import type { PrinterCutMode } from '@/stores/printing-v2.config';
import type { IStickerSettings } from '@/interfaces/IStickerSettings';
import { PRINTER_CODEPAGES } from '@/lib/print/codepages';

/**
 * Canonical per-printer hardware settings models, shared by the desktop
 * (bridge) and native (Capacitor) printing stores and by every document
 * renderer. Plain TS — no Vue, no strategy dependencies.
 */

export type PrinterModel = 'generic' | 'genericb' | 'epsona';
export type QrRenderMode = 'auto' | 'native' | 'image';
export type PrinterCutType = 'partial' | 'full';
export type ZplDpmm = 6 | 8 | 12 | 24;

export interface IEscposPrinterSettings {
  /** Bold/italic byte variants — maps onto EscPosEncoder's PrintFormat. */
  model: PrinterModel;
  codepage: string;
  paperWidthMm: 58 | 80;
  qrMode: QrRenderMode;
  /** Chars-per-line override for normal size; null = auto (width table). */
  charsPerLineNormal: number | null;
  /** Chars-per-line override for small size; null = auto (width table). */
  charsPerLineSmall: number | null;
  cutType: PrinterCutType;
  cutMode: PrinterCutMode;
  /**
   * Blank feed lines before the cut (paper margin). null = each document's
   * historical feed count (ticket/comanda 3, group 1, caja/day-close/invoice
   * 2) — unconfigured clients keep byte parity with pre-upgrade output.
   */
  feedLines: number | null;
}

export interface IZplPrinterSettings {
  dpmm: ZplDpmm;
  widthMm: number;
  heightMm: number;
  /** Label padding in dots (jszpl Spacing), legacy asymmetric defaults. */
  padding: { top: number; right: number; bottom: number; left: number };
  fontSize: 'xs' | 's' | 'm' | 'l';
  /** Whole-label offset in mm, applied via ^LH/^LS/^LT. */
  offsetXmm: number;
  offsetYmm: number;
}

/**
 * One printer's resolved hardware settings as projected into the legacy
 * printing store (desktop: keyed by bridge ref; native: keyed by name).
 * `zpl` absent on a zpl printer = resolve legacy localStorage at print time.
 */
export interface IPrinterSettingsEntry {
  escpos?: IEscposPrinterSettings;
  zpl?: IZplPrinterSettings;
}

export const DEFAULT_ESCPOS_SETTINGS: IEscposPrinterSettings = {
  model: 'generic',
  codepage: 'cp858',
  paperWidthMm: 80,
  qrMode: 'auto',
  charsPerLineNormal: null,
  charsPerLineSmall: null,
  cutType: 'partial',
  cutMode: 'modern',
  feedLines: null,
};

/** Matches the legacy LabelPrinter defaults (defaultStickerSettings). */
export const DEFAULT_ZPL_SETTINGS: IZplPrinterSettings = {
  dpmm: 12,
  widthMm: 55,
  heightMm: 44,
  padding: { top: 20, right: 10, bottom: 20, left: 220 },
  fontSize: 'm',
  offsetXmm: 0,
  offsetYmm: 0,
};

const PRINTER_MODELS: PrinterModel[] = ['generic', 'genericb', 'epsona'];
const QR_MODES: QrRenderMode[] = ['auto', 'native', 'image'];
const CUT_TYPES: PrinterCutType[] = ['partial', 'full'];
const CUT_MODES: PrinterCutMode[] = ['modern', 'legacy'];
const ZPL_DPMM_VALUES: ZplDpmm[] = [6, 8, 12, 24];
const CODEPAGE_SET = new Set<string>(PRINTER_CODEPAGES);

export const CHARS_PER_LINE_MIN = 8;
export const CHARS_PER_LINE_MAX = 96;

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function normalizeCharsPerLine(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return clampNumber(Math.floor(value), CHARS_PER_LINE_MIN, CHARS_PER_LINE_MAX);
}

function pickWhitelisted<T>(value: unknown, allowed: T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

function normalizeFiniteNumber(
  value: unknown,
  min: number,
  max: number,
  fallback: number
): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return clampNumber(value, min, max);
}

/** Clamp/whitelist every field; invalid or absent values fall to defaults. */
export function normalizeEscposSettings(
  partial?: Partial<IEscposPrinterSettings> | null
): IEscposPrinterSettings {
  const raw = partial ?? {};
  return {
    model: pickWhitelisted(raw.model, PRINTER_MODELS, 'generic'),
    codepage:
      typeof raw.codepage === 'string' && CODEPAGE_SET.has(raw.codepage)
        ? raw.codepage
        : DEFAULT_ESCPOS_SETTINGS.codepage,
    paperWidthMm: raw.paperWidthMm === 58 ? 58 : 80,
    qrMode: pickWhitelisted(raw.qrMode, QR_MODES, 'auto'),
    charsPerLineNormal: normalizeCharsPerLine(raw.charsPerLineNormal),
    charsPerLineSmall: normalizeCharsPerLine(raw.charsPerLineSmall),
    cutType: pickWhitelisted(raw.cutType, CUT_TYPES, 'partial'),
    cutMode: pickWhitelisted(raw.cutMode, CUT_MODES, 'modern'),
    feedLines:
      typeof raw.feedLines === 'number' && Number.isFinite(raw.feedLines)
        ? clampNumber(Math.floor(raw.feedLines), 0, 10)
        : null,
  };
}

/** Clamp/whitelist every field; invalid or absent values fall to defaults. */
export function normalizeZplSettings(
  partial?: Partial<IZplPrinterSettings> | null
): IZplPrinterSettings {
  const raw = partial ?? {};
  const rawPadding =
    raw.padding && typeof raw.padding === 'object' ? raw.padding : {};
  const defaults = DEFAULT_ZPL_SETTINGS;
  const paddingSide = (value: unknown, fallback: number): number =>
    typeof value === 'number' && Number.isFinite(value)
      ? clampNumber(Math.floor(value), 0, 400)
      : fallback;
  return {
    dpmm: pickWhitelisted(raw.dpmm, ZPL_DPMM_VALUES, defaults.dpmm),
    widthMm: normalizeFiniteNumber(raw.widthMm, 10, 200, defaults.widthMm),
    heightMm: normalizeFiniteNumber(raw.heightMm, 10, 200, defaults.heightMm),
    padding: {
      top: paddingSide(rawPadding.top, defaults.padding.top),
      right: paddingSide(rawPadding.right, defaults.padding.right),
      bottom: paddingSide(rawPadding.bottom, defaults.padding.bottom),
      left: paddingSide(rawPadding.left, defaults.padding.left),
    },
    fontSize: pickWhitelisted(
      raw.fontSize,
      ['xs', 's', 'm', 'l'],
      defaults.fontSize
    ),
    offsetXmm: normalizeFiniteNumber(raw.offsetXmm, -50, 50, 0),
    offsetYmm: normalizeFiniteNumber(raw.offsetYmm, -50, 50, 0),
  };
}

/**
 * Standard ESC/POS character widths at 203 DPI (the -pos table):
 * 58mm → normal 24 / small 32; 80mm → normal 32 / small 42.
 * Overrides are floored and clamped 8–96.
 */
export function resolveWidthInChars(
  paperWidthMm: number,
  sizeKind: 'normal' | 'small',
  overrides?: { normal?: number | null; small?: number | null }
): number {
  const override = normalizeCharsPerLine(
    sizeKind === 'small' ? overrides?.small : overrides?.normal
  );
  if (override !== null) return override;
  if (paperWidthMm <= 58) return sizeKind === 'small' ? 32 : 24;
  return sizeKind === 'small' ? 42 : 32;
}

/**
 * Whole-label offset commands, injected right after ^XA.
 * ^LH rejects negatives (firmware-dependent clamping) so negative X routes
 * through ^LS and negative Y through ^LT (clamped to its -120 firmware
 * floor). Because ^LH/^LS/^LT PERSIST across jobs on real Zebra firmware,
 * every label printed with EXPLICIT per-printer settings must emit the full
 * triplet (default ^LH0,0^LS0^LT0) — a calibration print must never
 * permanently offset later labels. Labels resolved from LEGACY settings emit
 * no triplet at all: byte parity with pre-upgrade output, and printer-side
 * (front panel / ZSU) offset calibration keeps applying.
 */
export function zplOffsetCommands(zpl: IZplPrinterSettings): string {
  const xDots = Math.round(zpl.offsetXmm * zpl.dpmm);
  const yDots = Math.round(zpl.offsetYmm * zpl.dpmm);
  const lh = `^LH${Math.max(xDots, 0)},${Math.max(yDots, 0)}`;
  const ls = `^LS${Math.min(xDots, 0)}`;
  const lt = `^LT${clampNumber(Math.min(yDots, 0), -120, 0)}`;
  return `${lh}${ls}${lt}`;
}

/** Injects the offset triplet right after the first ^XA of a label. */
export function injectZplOffsets(
  zplDocument: string,
  settings: IZplPrinterSettings
): string {
  const commands = zplOffsetCommands(settings);
  const index = zplDocument.indexOf('^XA');
  if (index === -1) return `${commands}${zplDocument}`;
  const insertAt = index + '^XA'.length;
  return `${zplDocument.slice(0, insertAt)}${commands}${zplDocument.slice(
    insertAt
  )}`;
}

/** Converts per-printer ZPL settings to the legacy IStickerSettings shape. */
export function zplSettingsToSticker(
  zpl: IZplPrinterSettings
): IStickerSettings {
  return {
    printDensity: `${zpl.dpmm}dpmm` as IStickerSettings['printDensity'],
    width: zpl.widthMm,
    height: zpl.heightMm,
    padding: { ...zpl.padding },
    fontSize: zpl.fontSize,
  };
}

/** Seeds per-printer ZPL settings from a legacy IStickerSettings payload. */
export function stickerToZplSettings(
  sticker: Partial<IStickerSettings> | null | undefined
): IZplPrinterSettings {
  const density = sticker?.printDensity;
  const dpmm =
    typeof density === 'string' ? Number(density.replace('dpmm', '')) : NaN;
  return normalizeZplSettings({
    dpmm: dpmm as ZplDpmm,
    widthMm: sticker?.width,
    heightMm: sticker?.height,
    padding: sticker?.padding,
    fontSize: sticker?.fontSize,
    offsetXmm: 0,
    offsetYmm: 0,
  });
}
